import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FaceScanDialog } from "@/components/FaceScanDialog";

export const timeOf = (iso) => iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "—";
export const statusStyle = {
  "On-Time": "bg-emerald-50 text-emerald-700", Early: "bg-blue-50 text-blue-700",
  Late: "bg-red-50 text-red-700", Flexible: "bg-purple-50 text-purple-700",
  Present: "bg-emerald-50 text-emerald-700", Absent: "bg-slate-100 text-slate-500",
};

export function ClockWidget({ onClocked }) {
  const [today, setToday] = useState({});
  const [site, setSite] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [now, setNow] = useState(new Date());
  const [busy, setBusy] = useState(false);
  const [faceReg, setFaceReg] = useState(true);
  const [scan, setScan] = useState({ open: false, mode: "verify" });
  const [pending, setPending] = useState(null);
  const [allowed, setAllowed] = useState(true);
  const [isSuper, setIsSuper] = useState(false);
  const [kioskOnly, setKioskOnly] = useState(false);
  const [kiosk, setKiosk] = useState(false);

  const load = useCallback(() => {
    api.get("/attendance/clock-access").then(({ data }) => { setAllowed(data.me_allowed !== false); setIsSuper(!!data.me_super); setKioskOnly(!!data.me_kiosk_only); setKiosk(!!data.kiosk); }).catch(() => {});
    api.get("/attendance/today").then(({ data }) => setToday(data || {})).catch(() => {});
    api.get("/attendance/my-site").then(({ data }) => { setSite(data.geofence); setSchedule(data.schedule); }).catch(() => {});
    api.get("/attendance/face-status").then(({ data }) => setFaceReg(!!data.registered)).catch(() => {});
  }, []);
  useEffect(() => { load(); const i = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(i); }, [load]);

  const getGeo = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported on this device"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => reject(new Error("Location permission denied. Enable GPS to clock in.")),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 });
  });

  // Step 1: validate GPS, then open the face scanner
  const startClock = async (type) => {
    setBusy(true);
    try {
      const geo = await getGeo().catch((e) => { if (isSuper) return {}; throw e; });
      if (isSuper) { setBusy(false); await submitClock({ type, geo }, {}); return; }
      setPending({ type, geo });
      setScan({ open: true, mode: faceReg ? "verify" : "register" });
    } catch (e) { toast.error(e.message || "Location error"); }
    finally { setBusy(false); }
  };

  // Step 2: face captured -> ask for the last 5 digits of the Biometric ID (final check)
  const [codeStep, setCodeStep] = useState(null);
  const [bioCode, setBioCode] = useState("");
  const onScan = (scanData) => { if (pending) { setBioCode(""); setCodeStep(scanData); } };
  // Step 3: send clock request
  const submitClock = async (req = pending, scanData = codeStep) => {
    const { descriptor, photo, liveness } = scanData;
    setCodeStep(null);
    setBusy(true);
    try {
      const body = { ...req.geo, descriptor, photo, liveness, bio_code: bioCode };
      if (req.type === "in") {
        const { data } = await api.post("/attendance/clock-in", body);
        data.face_verified ? toast.success("Clocked in — face verified") : toast.warning("Clocked in — face not verified, sent to HR for review");
      } else {
        const { data } = await api.post("/attendance/clock-out", body);
        const msg = `Clocked out · ${data.hours}h${data.overtime_hours ? ` (+${data.overtime_hours}h OT)` : ""}`;
        data.face_verified ? toast.success(msg) : toast.warning(`${msg} — face not verified, sent to HR`);
      }
      setPending(null); load(); onClocked && onClocked();
    } catch (e) { const d = e.response?.data?.detail; toast.error((typeof d === "string" ? d : d ? JSON.stringify(d) : e.message) || "Failed"); }
    finally { setBusy(false); }
  };

  const clockedIn = today?.clock_in && !today?.clock_out;
  const sessions = today?.sessions || [];
  const sessionCount = sessions.length;
  const faceUnverified = sessionCount
    ? sessions.some((s) => s.face_verified === false || s.face_verified_out === false)
    : today?.face_verified === false;

  return (
    <div className="bg-slate-900 rounded-xl p-6 text-white relative overflow-hidden" data-testid="clock-widget">
      {codeStep && (
        <div className="absolute inset-0 z-20 bg-slate-900/95 flex flex-col items-center justify-center gap-3 p-6" data-testid="clock-bio-step">
          <Icons.KeyRound className="h-6 w-6 text-blue-400" />
          <p className="text-sm text-center">Enter the <b>last 5 digits</b> of your Biometric ID to finish</p>
          <input autoFocus inputMode="numeric" maxLength={5} value={bioCode} onChange={(e) => setBioCode(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter" && bioCode.length === 5) submitClock(); }}
            className="w-40 text-center tracking-[0.5em] text-lg font-mono rounded-lg bg-white/10 border border-white/20 py-2 outline-none focus:border-blue-400" data-testid="clock-bio-code" />
          <div className="flex gap-2">
            <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10" onClick={() => { setCodeStep(null); setPending(null); }} data-testid="clock-bio-cancel">Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={bioCode.length !== 5} onClick={() => submitClock()} data-testid="clock-bio-submit">Confirm</Button>
          </div>
        </div>
      )}
      <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-blue-600/20 blur-2xl" />
      <div className="relative">
        <div className="text-xs uppercase tracking-wider text-slate-400">{fmtDate(now.toISOString())}</div>
        <div className="text-4xl font-semibold font-mono mt-2">{now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
        <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-300" data-testid="clock-site">
          <Icons.MapPin className="h-3.5 w-3.5 text-amber-400" />{!site && isSuper ? "Super Admin · any location" : site ? `${site.name} · ${site.radius_m >= 1000 ? `${(site.radius_m / 1000).toLocaleString()} km` : `${site.radius_m} m`}` : "No site assigned"}
          {schedule && <span className="ml-2 text-slate-400">· {schedule.name} {schedule.flexible ? "(Flexible)" : `${schedule.shift_start}–${schedule.shift_end}`}</span>}
        </div>

        {isSuper ? <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-300" data-testid="clock-super-exempt"><Icons.ShieldCheck className="h-3.5 w-3.5" />Super Admin · level, site, face and Biometric ID checks skipped</div> : !kioskOnly &&
        <div className="mt-2 flex items-center gap-1.5 text-xs" data-testid="face-status">
          <Icons.ScanFace className={`h-3.5 w-3.5 ${faceReg ? "text-emerald-400" : "text-amber-400"}`} />
          {faceReg ? <span className="text-slate-300">Face ID registered</span> : <span className="text-amber-300">Face setup required (first clock-in)</span>}
          {faceReg && <span className="ml-1 text-slate-500">· contact HR to reset</span>}
        </div>}

        <div className="mt-4 flex gap-2 text-sm">
          <div className="bg-white/10 rounded-lg px-3 py-2 flex-1"><div className="text-slate-400 text-xs">Clock In</div><div className="font-mono">{timeOf(today?.clock_in)}</div></div>
          <div className="bg-white/10 rounded-lg px-3 py-2 flex-1"><div className="text-slate-400 text-xs">Clock Out</div><div className="font-mono">{timeOf(today?.clock_out)}</div></div>
        </div>
        {today?.status && today?.clock_in && <div className="mt-2 flex flex-wrap items-center gap-2">
          <Badge className={`${statusStyle[today.status] || "bg-slate-100 text-slate-700"} text-[10px]`}>{today.status}{today.late_minutes ? ` · ${today.late_minutes}m late` : ""}</Badge>
          {sessionCount > 1 && <Badge className="bg-slate-100 text-slate-700 text-[10px]" data-testid="session-count">{sessionCount} sessions today</Badge>}
          {today.clock_out && today.hours != null && <span className="text-[11px] text-slate-400">{today.hours}h registered (first in → last out)</span>}
          {faceUnverified && <Badge className="bg-amber-50 text-amber-700 text-[10px]" data-testid="face-unverified-badge">Face unverified</Badge>}
        </div>}

        {!allowed && <div className="mt-4 rounded-lg bg-white/10 px-3 py-2.5 text-xs text-slate-200" data-testid="clock-not-allowed">{kioskOnly ? "Rank & File, OJT and Probationary employees clock in on the Shared Clock In/Out using their face and the last 5 digits of their Biometric ID." : "Clock In/Out is not enabled for your employee level. Your attendance comes from the biometric DTR."}</div>}
        {kiosk && <Link to="/hris/shared-clock" className="mt-3 flex items-center justify-center gap-1.5 rounded-lg border border-white/20 px-3 py-2 text-xs text-slate-200 hover:bg-white/10" data-testid="open-shared-clock-link"><Icons.MonitorSmartphone className="h-3.5 w-3.5" />Open Shared Clock In/Out</Link>}
        {allowed && !clockedIn && <Button onClick={() => startClock("in")} disabled={busy || (!site && !isSuper)} className="w-full mt-4 bg-blue-600 hover:bg-blue-700" data-testid="clock-in-button"><Icons.LogIn className="h-4 w-4 mr-1.5" /> {busy ? "Working…" : (today?.clock_in ? "Clock In (new session)" : "Clock In")}</Button>}
        {allowed && clockedIn && <Button onClick={() => startClock("out")} disabled={busy || (!site && !isSuper)} className="w-full mt-4 bg-amber-600 hover:bg-amber-700" data-testid="clock-out-button"><Icons.LogOut className="h-4 w-4 mr-1.5" /> {busy ? "Working…" : "Clock Out"}</Button>}
      </div>

      <FaceScanDialog
        open={scan.open}
        mode={scan.mode}
        onOpenChange={(v) => setScan((s) => ({ ...s, open: v }))}
        onResult={(res) => onScan(res)}
      />
    </div>
  );
}
